import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Send, Paperclip, Smile, MoreVertical, X, FileText, FileSpreadsheet, Download } from 'lucide-react';

const FilePreview = ({ file }) => {
  const type = file.mimetype || "";

  const isPDF = type === "application/pdf";
  const isExcel =
    type === "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    type === "application/vnd.ms-excel";

  let Icon = FileText;
  let bgColor = "bg-red-50";
  let iconColor = "text-red-600";

  if (isExcel) {
    Icon = FileSpreadsheet;
    bgColor = "bg-green-50";
    iconColor = "text-green-600";
  }

  return (
    <a
      href={file.publicUrl}
      target="_blank"
      rel="noopener noreferrer"
      className={`flex items-center space-x-3 p-3 rounded-lg border hover:bg-gray-50 transition max-w-xs ${bgColor}`}
    >
      <Icon className={`w-8 h-8 ${iconColor}`} />

      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{file.name}</p>
        <p className="text-xs text-gray-500">
          {isPDF ? "PDF Document" : isExcel ? "Excel Spreadsheet" : "File"}
        </p>
      </div>

      <Download className="w-4 h-4 text-gray-400" />
    </a>
  );
};

export const ChatArea = ({ chat, messages, onSendMessage, onLoadMore, loading }) => {
  const user = JSON.parse(localStorage.getItem("user"));
  const containerRef = useRef(null);
  const [newMessage, setNewMessage] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [isInitialScrollDone, setIsInitialScrollDone] = useState(false);
  const scrollRef = useRef({ prevScrollHeight: 0, prevScrollTop: 0 });

  // const handleSend = () => {
  //   if (newMessage.trim()) {
  //     onSendMessage(newMessage.trim());
  //     setNewMessage('');
  //   }
  // };

  const handleSend = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;

    // Send message along with attachments
    await onSendMessage(newMessage.trim(), attachments);

    setNewMessage('');
    setAttachments([]);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setAttachments((prev) => [...prev, ...files]);
    e.target.value = null; // reset input
  };

  const removeAttachment = (index) => {
    setAttachments((prev) => prev.filter((_, i) => i !== index));
  };

  // Scroll to bottom when chat changes for the first time
  useEffect(() => {
    if (containerRef.current && !isInitialScrollDone) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
      setIsInitialScrollDone(true);
    }
  }, [chat?._id]);

  const handleScroll = () => {
  if (containerRef.current.scrollTop === 0 && onLoadMore && !loading) {
    // Save scroll position before fetching older messages
    scrollRef.current.prevScrollHeight = containerRef.current.scrollHeight;
    scrollRef.current.prevScrollTop = containerRef.current.scrollTop;
    onLoadMore();
  }
};

  // Preserve scroll position when older messages load
  useLayoutEffect(() => {
  if (!containerRef.current) return;

  if (scrollRef.current.prevScrollHeight && messages.length) {
    const diff = containerRef.current.scrollHeight - scrollRef.current.prevScrollHeight;
    containerRef.current.scrollTop = diff + scrollRef.current.prevScrollTop;

    // Reset
    scrollRef.current.prevScrollHeight = 0;
    scrollRef.current.prevScrollTop = 0;
  } else if (chat?._id && messages.length && !scrollRef.current.prevScrollHeight) {
    // Scroll to bottom on initial chat load
    containerRef.current.scrollTop = containerRef.current.scrollHeight;
  }
}, [messages, chat?._id]);

  const otherUser = chat?.participants?.find(
      (p) => p.user?._id !== user?._id
    )?.user;

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateLabel = (date) => {
    const today = new Date();
    const msgDate = new Date(date);
    const diffTime = today.setHours(0,0,0,0) - msgDate.setHours(0,0,0,0);
    const diffDays = Math.round(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Yesterday";
    return msgDate.toLocaleDateString();
  };

   const groupMessagesByDate = (messages) => {
    const grouped = [];
    let lastDateLabel = null;

    messages.slice().reverse().forEach((msg) => {
      const dateLabel = formatDateLabel(msg.createdAt);
      if (dateLabel !== lastDateLabel) {
        grouped.push({ type: 'date', label: dateLabel, id: `date-${msg._id}` });
        lastDateLabel = dateLabel;
      }
      grouped.push({ type: 'message', ...msg });
    });

    return grouped;
  };

  if (!chat) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center text-gray-500">
          <div className="w-24 h-24 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
            <Send className="w-12 h-12 text-gray-400" />
          </div>
          <h3 className="text-lg font-medium mb-2">Select a conversation</h3>
          <p>Choose a conversation from the sidebar to start messaging</p>
        </div>
      </div>
    );
  }

  const groupedMessages = groupMessagesByDate(messages);
  return (
    <div className="flex-1 flex flex-col">
      {/* Chat Header */}
      <div className="flex items-center justify-between p-4 card-wrapper !rounded-none">
        <div className="flex items-center space-x-3">
            {
              otherUser?.profileImageLink ? (
                <img
                  src={otherUser.profileImageLink}
                  alt="user"
                  className="w-12 h-12 rounded-full object-cover"
                />
              ) : (
                <div className="w-12 h-12 bg-gradient-to-br from-[#004877] to-purple-200 rounded-full flex items-center justify-center text-white font-medium">
                  {otherUser?.firstName || otherUser?.lastName
                    ? `${otherUser.firstName?.[0] || ""}${otherUser.lastName?.[0] || ""}`.toUpperCase()
                    : otherUser?.email
                    ? otherUser?.email[0].toUpperCase()
                    : "?"}
                </div>
              )
            }
          <div>
            <h3 className="font-medium text-gray-900">{chat.name || otherUser?.firstName + " " + otherUser?.lastName}</h3>
            {/* <p className="text-sm text-green-500">Online</p> */}
          </div>
        </div>
        <button className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
          <MoreVertical className="w-5 h-5" />
        </button>
      </div>

      {/* Messages Area */}
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className="flex-1 !overflow-y-auto p-4 space-y-4 card-wrapper !rounded-none"
      >
        {/* {messages.slice().reverse().map((message) => {
          const isOwnMessage = message.sender === user._id;
          return (
            <div
              key={message._id || message.id}
              className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  isOwnMessage
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-900 shadow-sm border border-gray-200'
                }`}
              >
                <p className="text-sm">{message.text}</p>
                <p className={`text-xs mt-1 ${isOwnMessage ? 'text-blue-100' : 'text-gray-500'}`}>
                  {formatTime(message.createdAt)}
                </p>
              </div>
            </div>
          );
        })} */}
        {groupedMessages.map((item) => {
          if (item.type === 'date') {
            return (
              <div key={item.id} className="text-center text-gray-400 text-sm my-2">
                {item.label}
              </div>
            );
          }
          const isOwnMessage = item.sender === user._id;
          return (
            <div
              key={item._id || item.id}
              className={`flex ${isOwnMessage ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                  isOwnMessage
                    ? 'bg-[#004877] text-white'
                    : 'bg-white text-gray-900 shadow-sm border border-gray-200'
                }`}
              >
                <p className="text-sm">{item.text}</p>

                {item.attachments && item.attachments.length > 0 && (
  <div className="mt-2 flex flex-wrap gap-2">
    {item.attachments.map((att, i) => {
      const type = att.mimetype || ""; // MIME type from backend
      const isImage = type.startsWith("image/");
      const isVideo = type.startsWith("video/");
      const isPDF = type === "application/pdf";
      const isExcel =
        type ===
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
        type === "application/vnd.ms-excel";

      return (
        <div key={i} className="flex flex-col items-start">
          {isImage && (
            <img
              src={att.publicUrl}
              alt={att.name}
              className="w-32 h-32 object-cover rounded border"
            />
          )}
          {isVideo && (
            <video
              src={att.publicUrl}
              controls
              className="w-48 h-32 rounded border"
            />
          )}
          {/* {(isPDF || isExcel || (!isImage && !isVideo)) && (
            <a
              target='_blank'
              href={att.publicUrl}
              download={att.name}
              className="bg-gray-100 px-2 py-1 rounded text-xs hover:underline"
            >
              {att.name}
            </a>
          )} */}
          {(isPDF || isExcel) && (
            <FilePreview file={att} />
          )}

          {(!isImage && !isVideo && !isPDF && !isExcel) && (
            <a
              href={att.publicUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="bg-gray-100 px-2 py-1 rounded text-xs hover:underline"
            >
              {att.name}
            </a>
          )}
        </div>
      );
    })}
  </div>
)}

                <p className={`text-xs mt-1 ${isOwnMessage ? 'text-blue-100' : 'text-gray-500'}`}>
                  {formatTime(item.createdAt)}
                </p>
              </div>
            </div>
          );
        })}

        {messages.length === 0 && (
          <div className="text-center text-gray-500 py-8">
            <p>No messages yet. Start the conversation!</p>
          </div>
        )}
      </div>

      {/* Message Input */}
      {/* <div className="p-4 card-wrapper !rounded-none border-t border-gray-200">
        <div className="flex items-center space-x-3">
          <button className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
            <Paperclip className="w-5 h-5" />
          </button>
          <div className="flex-1 rounded-lg">
            <textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Type a message..."
              className="w-full px-3 py-2 border-0 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:outline-none card-wrapper"
              rows={1}
              style={{ minHeight: '40px', maxHeight: '120px' }}
            />
          </div>
          <button className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
            <Smile className="w-5 h-5" />
          </button>
          <button
            onClick={handleSend}
            disabled={!newMessage.trim()}
            className={`p-2 rounded-full transition-colors ${
              newMessage.trim()
                ? 'bg-[#004877] text-white hover:bg-blue-600'
                : 'bg-gray-200 text-gray-400 cursor-not-allowed'
            }`}
          >
            <Send className="w-5 h-5" />
          </button>
        </div>
      </div> */}
      {/* Message Input */}
      <div className="p-4 card-wrapper !rounded-none border-t border-gray-200">
        <div className="flex flex-col space-y-2">
          {attachments.length > 0 && (
            <div className="flex space-x-2 overflow-x-auto">
              {attachments.map((file, i) => (
                <div
                  key={i}
                  className="relative flex items-center bg-gray-100 p-2 rounded"
                >
                  <span className="text-sm">{file.name}</span>
                  <button
                    onClick={() => removeAttachment(i)}
                    className="absolute -top-2 -right-2 w-4 h-4 flex items-center justify-center rounded-full bg-gray-300 hover:bg-gray-400 text-white text-xs"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}
            </div>
          )}

          <div className="flex items-center space-x-3">
            <label className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100 cursor-pointer">
              <Paperclip className="w-5 h-5" />
              <input
                type="file"
                multiple
                className="hidden"
                onChange={handleFileSelect}
              />
            </label>

            <div className="flex-1 rounded-lg">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                className="w-full px-3 py-2 border-0 rounded-lg resize-none focus:ring-2 focus:ring-blue-500 focus:outline-none card-wrapper"
                rows={1}
                style={{ minHeight: '40px', maxHeight: '120px' }}
              />
            </div>

            <button className="p-2 text-gray-400 hover:text-gray-600 rounded-full hover:bg-gray-100">
              <Smile className="w-5 h-5" />
            </button>
            <button
              onClick={handleSend}
              disabled={!newMessage.trim() && attachments.length === 0}
              className={`p-2 rounded-full transition-colors ${
                newMessage.trim() || attachments.length > 0
                  ? 'bg-[#004877] text-white hover:bg-blue-600'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              <Send className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
